import Glide from '@glidejs/glide';
import '@glidejs/glide/dist/css/glide.core.min.css';
import initEthosCarousels from '../util/ethosCarousel';

/**
 * Smooth scrolling for the sticky in-page framework nav.
 *
 * Each nav button links to a framework <section id="…"> further up the page.
 * By default the browser jumps there instantly; this intercepts the click and
 * animates the scroll, landing the section top flush with the bottom of the
 * sticky page header (its height plus the WP admin bar when present) rather
 * than the fixed 120px CSS offset that left a gap above the section.
 */
function initStickyNavScroll() {
    const header = document.querySelector('[data-header-2026]');

    // Distance from the viewport top to the bottom of the sticky header:
    // the header bar (h-16 = 64px) plus the admin bar height for logged-in users.
    const headerOffset = () => {
        const adminBar =
            parseInt(getComputedStyle(document.body).getPropertyValue('--wp-admin-bar-height'), 10) || 0;
        return adminBar + (header ? header.offsetHeight : 0);
    };

    document
        .querySelectorAll('nav[aria-label="Available frameworks"] a[href^="#"]')
        .forEach((link) => {
            link.addEventListener('click', (event) => {
                const id = link.getAttribute('href').slice(1);
                const target = document.getElementById(id);
                if (!target) return;

                event.preventDefault();

                const top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
                window.scrollTo({ top, behavior: 'smooth' });

                // Keep the URL hash in sync without re-triggering a native jump.
                history.pushState(null, '', `#${id}`);
            });
        });
}

/**
 * Hide the sticky nav once the footer has scrolled over it.
 *
 * The nav is rendered inside the layout's z-10 content wrapper, so its z-index
 * is capped by that stacking context. The footer (also z-10, but later in the
 * DOM) covers it correctly, but the footer's fixed logo reveal sits at z-0 —
 * intentionally below the page content, since it is only meant to show through
 * the transparent spacer past the footer. That puts the nav above the reveal,
 * so it reappears over the logo. No z-index value satisfies both, so the nav is
 * hidden outright at the point the footer has fully covered it — which is
 * invisible to the user, because it is already out of sight by then.
 */
function initStickyNavFooterHide() {
    const nav = document.querySelector('[data-framework-sticky-nav]');
    const footer = document.querySelector('.footer-2026');
    if (!nav || !footer) return;

    const hiddenClasses = ['invisible', 'opacity-0'];
    let ticking = false;

    const update = () => {
        ticking = false;

        // The nav is fully obscured once the footer's top edge has risen past
        // the top of the nav bar.
        const covered = footer.getBoundingClientRect().top <= window.innerHeight - nav.offsetHeight;
        hiddenClasses.forEach((className) => nav.classList.toggle(className, covered));
    };

    const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
}

/**
 * Testimonials carousel.
 *
 * The framework template reuses the homepage testimonials component
 * (partials/home/testimonials-carousel.blade.php), so this mirrors the Glide
 * setup from routes/templateHome.js → initTestimonialsCarousels().
 */
function initTestimonialsCarousels() {
    document.querySelectorAll('.js-testimonials-carousel').forEach((el) => {
        const glide = new Glide(el, {
            type: 'carousel',
            perView: 1,
            gap: 20,
            animationDuration: 500,
        });

        // Arrows live as siblings of the glide element inside the same flex row.
        const row = el.parentElement;
        const prev = row?.querySelector('[data-prev]');
        const next = row?.querySelector('[data-next]');

        if (prev) prev.addEventListener('click', () => glide.go('<'));
        if (next) next.addEventListener('click', () => glide.go('>'));

        glide.mount();
    });
}

export default {
    init() {
        initTestimonialsCarousels();
        initEthosCarousels();
        initStickyNavScroll();
        initStickyNavFooterHide();
    },
    finalize() {},
};
